import type { Row } from '@tanstack/react-table'
import { FileSearchIcon, WrenchIcon } from 'lucide-react'

import { Button, DataTable, type DataTableColumns } from '@/components/ui'
import type { TableData } from '@/components/ui/table/DataTable/types'
import { RECONCILIATION_STATUSES } from '@/modules/projects/constants'
import type { CycleAccountRow } from '../cycleRows'
import { cycleColumns } from './columns'

// A cycle the engine could not balance is work to do, so it asks to be fixed. Every other cycle, and
// every cycle at all without the reconciliation feature, just offers a read of the engine's working.
const ReviewButton = ({
  row,
  isReconciliationEntitled,
  onOpen,
}: {
  row: CycleAccountRow
  isReconciliationEntitled: boolean
  onOpen: () => void
}) => {
  const isFixable =
    isReconciliationEntitled &&
    row.status === RECONCILIATION_STATUSES.needsReview
  const Icon = isFixable ? WrenchIcon : FileSearchIcon

  return (
    <Button
      theme={isFixable ? 'default' : 'outline'}
      size="sm"
      className="h-7 cursor-pointer gap-1.5 whitespace-nowrap px-2 text-xs"
      onClick={(event) => {
        event.stopPropagation()
        onOpen()
      }}
    >
      <Icon className="size-3.5" />
      {isFixable ? 'Review & fix' : 'View cycle'}
    </Button>
  )
}

interface CycleTableProps {
  rows: CycleAccountRow[]
  isReconciliationEntitled: boolean
  // What the user typed in the search box. The table both narrows to it and marks what matched.
  query: string
  onOpen: (row: CycleAccountRow) => void
  emptyMessage: string
}

const cycleOf = (row: Row<TableData>) => row.original as CycleAccountRow

// Every billing cycle, one line per account, on the same table the rest of the app uses. Its action
// floats over the right of each row and rides the sideways scroll, so it is never scrolled away.
export const CycleTable = ({
  rows,
  isReconciliationEntitled,
  query,
  onOpen,
  emptyMessage,
}: CycleTableProps) => (
  <div className="flex flex-col overflow-hidden rounded-lg border bg-white">
    <DataTable
      columns={cycleColumns(isReconciliationEntitled) as DataTableColumns}
      data={rows}
      height="max-h-[60vh]"
      globalFilter={query}
      emptyMessage={emptyMessage}
      enableRowSelection={false}
      onRowClick={(row) => onOpen(cycleOf(row))}
      rowAction={(row) => (
        <ReviewButton
          row={cycleOf(row)}
          isReconciliationEntitled={isReconciliationEntitled}
          onOpen={() => onOpen(cycleOf(row))}
        />
      )}
      scrollbarClassName="overflow-auto scrollbar-thin scrollbar-thumb-neutral-300 hover:scrollbar-thumb-neutral-400 scrollbar-track-transparent"
    />
  </div>
)
